"use client"

export const dynamic = 'force-dynamic'

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Globe, Heart, Users, Zap, Target, Award, DollarSign, Infinity, TrendingDown, AlertTriangle } from "lucide-react"
import Link from "next/link"
import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <Navigation />

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4">
            <Infinity className="w-3 h-3 mr-1" />
            Localization Without Limits, AI Without Markup
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Democratizing Localization for Everyone
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            We believe enterprise-scale localization shouldn't cost enterprise prices. Our mission is to make AI-powered translation accessible to every developer, startup, and organization.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
            <CardHeader>
              <Target className="h-8 w-8 text-blue-600 mb-2" />
              <CardTitle>Our Mission</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                To eliminate the cost barriers that prevent developers from building global applications. By removing markup fees, usage caps, and per-word pricing, we're making professional localization accessible to teams of any size - from solo developers to enterprise companies.
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400 dark:hover:border-green-600">
            <CardHeader>
              <Globe className="h-8 w-8 text-green-600 mb-2" />
              <CardTitle>Our Vision</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                A world where every application speaks every language - not because the developers can afford enterprise localization platforms, but because localization is as affordable as hosting. Where geographic reach isn't limited by budget.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* The Problem */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-orange-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                The Localization Industry Has a Problem
              </h2>
              <p className="text-xl text-gray-600 mb-12">
                Enterprise platforms charge excessive fees for basic functionality
              </p>
              
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="p-6 bg-white border border-orange-200">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                    <h3 className="text-xl font-semibold text-gray-900">The Problem</h3>
                  </div>
                  <div className="text-3xl font-bold text-orange-600 mb-4">$200-500/month</div>
                  <ul className="text-gray-600 space-y-2 text-left">
                    <li>• Lokalise charges $140-270/month base fees</li>
                    <li>• Plus $14-27 per additional user</li>
                    <li>• Limited to 5K-10K hosted keys</li>
                    <li>• Professional services at $150-300/hour</li>
                    <li>• Small teams pay the same per-user costs as enterprises</li>
                  </ul>
                </Card>
                
                <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <h3 className="text-xl font-semibold text-gray-900">Our Solution</h3>
                  </div>
                  <div className="text-3xl font-bold text-green-600 mb-4">$22-30/month</div>
                  <ul className="text-gray-600 space-y-2 text-left">
                    <li>• Bring your own OpenAI key (zero markup)</li>
                    <li>• $20/month platform fee - that's it</li>
                    <li>• Unlimited team members included</li>
                    <li>• Zero setup costs or per-key limits</li>
                    <li>• Enterprise features for everyone</li>
                  </ul>
                </Card>
              </div>
              
              <div className="mt-8 p-6 bg-blue-100 rounded-xl border border-blue-200">
                <div className="flex items-center justify-center mb-2">
                  <TrendingDown className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="text-blue-800 font-semibold text-lg">
                    We're democratizing localization by eliminating per-user fees and artificial limits
                  </span>
                </div>
                <p className="text-blue-700 text-sm">
                  Save 85-95% compared to enterprise platforms
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Impact */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Impact</h2>
                <p className="text-xl text-gray-600">
                  Real savings for real teams around the world
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8">
                <Card className="text-center p-6 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400">
                  <div className="text-4xl font-bold text-green-600 mb-2">85-95%</div>
                  <div className="text-lg font-semibold text-gray-900 mb-2">Cost Reduction</div>
                  <p className="text-gray-600">
                    Average savings compared to enterprise platforms like Lokalise
                  </p>
                </Card>
                
                <Card className="text-center p-6 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400">
                  <div className="text-4xl font-bold text-blue-600 mb-2">$3K+</div>
                  <div className="text-lg font-semibold text-gray-900 mb-2">Saved Per Customer</div>
                  <p className="text-gray-600">
                    Average annual savings for teams switching from enterprise platforms
                  </p>
                </Card>
                
                <Card className="text-center p-6 hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-purple-500/20 hover:border-purple-400">
                  <div className="text-4xl font-bold text-purple-600 mb-2">∞</div>
                  <div className="text-lg font-semibold text-gray-900 mb-2">No Limits</div>
                  <p className="text-gray-600">
                    Unlimited everything - projects, languages, team members, hosted keys
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Why We Built This */}
        <div className="max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-center mb-8">Why We Built Locsy</h2>
          <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
            <CardContent className="p-8">
              <p className="text-muted-foreground mb-4">
                As developers, we were shocked by localization pricing. We were paying <strong>$2,000-5,000 per month</strong> to enterprise platforms for features that cost <strong>$22-30 in actual AI processing</strong>. The rest was pure markup.
              </p>
              <p className="text-muted-foreground mb-4">
                When OpenAI's advanced language models became available, we realized we could cut out the middleman entirely. Why pay 100x markup when you can pay the actual cost and get better, faster translations?
              </p>
              <p className="text-muted-foreground">
                Our "Bring Your Own Key" approach eliminates markup while giving you complete transparency and control. We handle the complexity, security, and developer experience - you get enterprise-scale localization at startup cost.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Values */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
              <CardHeader className="text-center">
                <DollarSign className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <CardTitle>Cost Transparency</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground text-sm">
                  You should know exactly what you're paying for. No hidden fees, no markup, no surprises. Just honest pricing that scales with your actual usage.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-green-500/20 hover:border-green-400 dark:hover:border-green-600">
              <CardHeader className="text-center">
                <Users className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <CardTitle>Developer-First</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground text-sm">
                  Every feature is designed with developers in mind. Clean APIs, comprehensive documentation, and tools that integrate seamlessly into your workflow.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-purple-500/20 hover:border-purple-400 dark:hover:border-purple-600">
              <CardHeader className="text-center">
                <Infinity className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <CardTitle>No Artificial Limits</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground text-sm">
                  Unlimited users, languages, projects, and features. We don't believe in artificial scarcity or tiered pricing that holds back your growth.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Team */}
        <div className="max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-center mb-8">Our Team</h2>
          <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-blue-500/20 hover:border-blue-400 dark:hover:border-blue-600">
            <CardContent className="p-8">
              <p className="text-muted-foreground text-center mb-6">
                We're a team of engineers, designers, and localization experts who experienced firsthand the pain of enterprise pricing. We're building the localization platform we wished existed.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Badge variant="secondary">Former Enterprise Platform Users</Badge>
                <Badge variant="secondary">AI/ML Specialists</Badge>
                <Badge variant="secondary">Full-Stack Engineers</Badge>
                <Badge variant="secondary">DevOps Engineers</Badge>
                <Badge variant="secondary">UX Designers</Badge>
                <Badge variant="secondary">Cost Optimization Experts</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recognition */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-12">Recognition</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-yellow-500/20 hover:border-yellow-400 dark:hover:border-yellow-600">
              <CardHeader className="text-center">
                <Award className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                <CardTitle>Best Developer Tool 2025</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground text-sm">
                  Recognized by Dev Awards for disrupting enterprise localization pricing and democratizing access to AI translation.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-orange-500/20 hover:border-orange-400 dark:hover:border-orange-600">
              <CardHeader className="text-center">
                <Award className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                <CardTitle>Product Hunt #1</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground text-sm">
                  Featured as Product of the Day with developers celebrating an alternative to expensive enterprise platforms.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all duration-200 hover:ring-2 hover:ring-red-500/20 hover:border-red-400 dark:hover:border-red-600">
              <CardHeader className="text-center">
                <Award className="h-8 w-8 text-red-600 mx-auto mb-2" />
                <CardTitle>Open Source Champion</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground text-sm">
                  Supporting the open source community with free localization for qualifying projects.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Join the Localization Revolution</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Break free from enterprise pricing and build global applications without breaking the bank. 
            See how much you can save compared to traditional localization platforms.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/auth/signup" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 text-base h-12 px-8">
              Start Saving Today - Free Trial
            </Link>
            <Link href="/calculator" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 text-base h-12 px-8">
              Calculate Your Savings
            </Link>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            Join thousands of developers who've cut their localization costs by 95%
          </p>
        </div>
      </section>

      <Footer />
    </div>
  )
} 